import React from "react";
import ReactDOM from "react-dom";
import ReactExport from "react-export-excel";
import { languages } from "../../constants/languages";
import { groupBy } from "../../util/GroupBy";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

export const exportPlayers = async (tournament, divisionsObject) => {
  try {
    // Get all teams first to organize players by division
    const teamsSnapshot = await window.firebase
      .firestore()
      .collection("tournaments")
      .doc(tournament[".key"])
      .collection("teams")
      .get();

    const teams = [];
    teamsSnapshot.forEach((doc) => {
      let team = doc.data();
      team.id = doc.id;
      teams.push(team);
    });

    // Get all players
    const playersSnapshot = await window.firebase
      .firestore()
      .collection("tournaments")
      .doc(tournament[".key"])
      .collection("players")
      .get();

    // Get all player contact info
    const playersContactInfoSnapshot = await window.firebase
      .firestore()
      .collection("tournaments")
      .doc(tournament[".key"])
      .collection("playersContactInfo")
      .get();

    // Create a map of player contact info
    const playersContactInfo = {};
    playersContactInfoSnapshot.forEach((doc) => {
      playersContactInfo[doc.id] = doc.data();
    });

    const players = [];
    playersSnapshot.forEach((doc) => {
      let player = doc.data();
      player.id = doc.id;
      // Find and add team info to player
      const team = teams.find((t) => t.id === player.team);
      if (team) {
        player.teamName = team.name;
        player.division = team.division;
        // Ensure infoFields and infoFieldsForTable are properly structured
        player.infoFields = player.infoFields || {};
        player.infoFieldsForTable = {};
        // Move top-level fields into infoFields if they exist
        if (player.age) player.infoFields.age = player.age;
        if (player.number) player.infoFields.number = player.number;

        // Add contact info fields
        const contactInfo = playersContactInfo[player.id] || {};
        Object.keys(tournament.infoFieldsPlayers || {})
          .filter((key) => ["name", "age", "number"].indexOf(key) === -1)
          .forEach((key) => {
            if (contactInfo[key] !== undefined) {
              player.infoFieldsForTable[key] = contactInfo[key];
            }
          });
      }
      players.push(player);
    });

    // Group players by division
    const playersPerDivision = groupBy(players, "division");

    // Prepare columns
    let columns = [
      { id: "name", label: languages.general.name },
      { id: "teamName", label: languages.results.team },
    ];

    // Add age column if enabled
    if (
      ((tournament.infoFieldsPlayers || {}).age || {}).show === undefined ||
      ((tournament.infoFieldsPlayers || {}).age || {}).show === true
    ) {
      columns.push({
        id: "age",
        label:
          ((tournament.infoFieldsPlayers || {}).age || {}).name ||
          languages.teams.age,
      });
    }

    // Add number column if enabled
    if (
      ((tournament.infoFieldsPlayers || {}).number || {}).show === undefined ||
      ((tournament.infoFieldsPlayers || {}).number || {}).show === true
    ) {
      columns.push({
        id: "number",
        label:
          ((tournament.infoFieldsPlayers || {}).number || {}).name ||
          languages.teams.number,
      });
    }

    // Add custom info fields
    Object.keys(tournament.infoFieldsPlayers || {})
      .filter((key) => ["name", "age", "number"].indexOf(key) === -1)
      .forEach((key) => {
        const infoField = tournament.infoFieldsPlayers[key];
        if (infoField.show !== false) {
          // Show field unless explicitly hidden
          columns.push({
            id: key,
            label: infoField.name,
          });
        }
      });

    // Create Excel component
    const ExcelComponent = () => (
      <ExcelFile
        element={
          <button style={{ display: "none" }} id="downloadExcelButton">
            Download
          </button>
        }
        filename={languages.teams.players + " - " + tournament.name}
      >
        {Object.keys(playersPerDivision)
          .sort((aKey, bKey) => {
            let a = divisionsObject[aKey] || {};
            let b = divisionsObject[bKey] || {};
            if (a.num > b.num) return 1;
            if (a.num < b.num) return -1;
            if (a.id > b.id) return 1;
            if (a.id < b.id) return -1;
            return 0;
          })
          .map((divisionId) => {
            const division = divisionsObject[divisionId] || {};
            // Sort players by team name first, then player name
            const sortedPlayers = playersPerDivision[divisionId].sort(
              (a, b) => {
                if (a.teamName < b.teamName) return -1;
                if (a.teamName > b.teamName) return 1;
                return a.name.localeCompare(b.name, undefined, {
                  numeric: true,
                  sensitivity: "base",
                });
              }
            );

            return (
              <ExcelSheet
                key={divisionId}
                data={sortedPlayers}
                name={(division.name || "").replace(/[^a-zA-Z0-9 ]/g, "")}
              >
                {columns.map((column) => (
                  <ExcelColumn
                    key={column.id}
                    label={column.label}
                    value={(player) => {
                      if (column.id === "teamName")
                        return player.teamName || "";
                      if (column.id === "name") return player.name || "";
                      if (column.id === "age")
                        return (player.infoFields || {}).age || "";
                      if (column.id === "number")
                        return (player.infoFields || {}).number || "";
                      // For custom info fields
                      return (player.infoFieldsForTable || {})[column.id] || "";
                    }}
                  />
                ))}
              </ExcelSheet>
            );
          })}
      </ExcelFile>
    );

    // Create a temporary div and render the Excel component
    const tempDiv = document.createElement("div");
    document.body.appendChild(tempDiv);

    // Render the Excel component
    ReactDOM.render(<ExcelComponent />, tempDiv);

    // Trigger the download by clicking the hidden button
    setTimeout(() => {
      const downloadButton = document.getElementById("downloadExcelButton");
      if (downloadButton) {
        downloadButton.click();
      }

      // Cleanup
      setTimeout(() => {
        ReactDOM.unmountComponentAtNode(tempDiv);
        document.body.removeChild(tempDiv);
      }, 100);
    }, 0);
  } catch (error) {
    console.error("Error exporting players:", error);
  }
};

// Wrapper component for backward compatibility
const ExportPlayersContainer = ({
  tournament,
  divisionsObject,
  autoExport,
}) => {
  React.useEffect(() => {
    if (autoExport) {
      exportPlayers(tournament, divisionsObject);
    }
  }, [autoExport, tournament, divisionsObject]);

  return null;
};

export default ExportPlayersContainer;
